import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Input, Icon, Avatar } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { prepare, members, create, edit, uploadImage, removeImage, get } from "../../api/Review"

/* Widgets */
import { toast } from "../common"
import Stars from "./Stars"

/* Shared */
import { RATE_WORDS, rateAccentOf, photoOf } from "./shared"
import { organizationIcon } from "../organization/shared"

/* Constants */
import { env } from "../../constants"

/* Moment */
import moment from "moment"

Modal.setAppElement("#root")


/* The longest list of the points the search shows at once */
const SHOWN = 40

/* The words of the errors of the backend */
const ERRORS = {
    ORGANIZATION_REQUIRED: "Выберите точку",
    INVALID_USER: "Введите имя клиента",
    INVALID_RATE: "Поставьте оценку",
    INVALID_DATE: "Дата не может быть в будущем"
}


/* Widget Review Form — a review added by hand (moved from 2GIS, written down from a call) and the
   change of any review. It is opened by the reference: open(null) — a new review, open(review) —
   the change of that one. A new review keeps its photos here until the save, an old one sends
   every added or removed photo right away */
class ReviewForm extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            id: 0,
            client: false,

            organizations: [],
            organizationsStatus: "loading",
            organization: null,
            picking: false,
            search: "",

            team: [],
            teamStatus: "idle",
            memberID: 0,
            member: "",

            user: "",
            rate: 0,
            comment: "",
            date: "",

            /* A new review: the files with their previews. An old one: the names on the server */
            files: [],
            images: [],
            uploading: false,

            saving: false
        }

        this._file = null
    }

    open = review => {

        const fields = {
            visible: true,
            picking: false,
            search: "",
            files: [],
            images: [],
            uploading: false,
            saving: false
        }

        if (review) {
            this.setState({
                ...fields,
                id: review.id,
                client: !!review.userID,
                organization: { id: review.organizationID, name: review.organization, image: review.organizationImage },
                memberID: review.memberID ? review.memberID : 0,
                member: review.member ? review.member : "",
                user: review.user ? review.user : "",
                rate: parseInt(review.rate) || 0,
                comment: review.comment ? review.comment : "",
                date: moment(review.createdAt).format("YYYY-MM-DDTHH:mm")
            }, () => {
                this.loadTeam(review.organizationID)
                this.loadImages(review.id)
            })
        }
        else {
            this.setState({
                ...fields,
                id: 0,
                client: false,
                organization: null,
                picking: true,
                team: [],
                teamStatus: "idle",
                memberID: 0,
                member: "",
                user: "",
                rate: 0,
                comment: "",
                date: ""
            })
        }

        if (this.state.organizations.length === 0) {
            this.loadOrganizations()
        }
    }

    close = () => {

        if (this.state.saving) {
            return
        }

        this.state.files.forEach(file => URL.revokeObjectURL(file.preview))
        this.setState({ visible: false })
    }

    loadOrganizations = () => {

        this.setState({ organizationsStatus: "loading" })

        prepare().then(response => {
            if (response && response.status === 200) {
                this.setState({ organizations: response.data.organizations, organizationsStatus: "ok" })
            }
            else {
                this.setState({ organizationsStatus: "error" })
            }
        }).catch(() => this.setState({ organizationsStatus: "error" }))
    }

    loadTeam = organizationID => {

        this.setState({ teamStatus: "loading", team: [] })

        members({ organizationID }).then(response => {
            if (response && response.status === 200) {
                this.setState({ team: response.data.members.filter(member => member.memberID), teamStatus: "ok" })
            }
            else {
                this.setState({ teamStatus: "error" })
            }
        }).catch(() => this.setState({ teamStatus: "error" }))
    }

    loadImages = id => {
        get({ id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ images: response.data.images.map(image => image.image) })
            }
        }).catch(() => { })
    }

    /* A point is chosen — its team comes, the master of another point does not stay */
    choose = organization => {

        const { organization: previous } = this.state

        const same = previous && parseInt(previous.id) === parseInt(organization.id)

        this.setState({ organization, picking: false, search: "", memberID: same ? this.state.memberID : 0, member: same ? this.state.member : "" })

        if (!same) {
            this.loadTeam(organization.id)
        }
    }

    /* The points matching the search — by the name or the digits of the phone */
    found = () => {

        const { organizations, search } = this.state

        const text = search.trim().toLowerCase()
        const digits = text.replace(/\D/g, "")

        if (text.length === 0) {
            return organizations.slice(0, SHOWN)
        }

        return organizations.filter(item => {

            if (`${item.name}`.toLowerCase().includes(text)) {
                return true
            }

            return digits.length >= 3 && `${item.phone}`.replace(/\D/g, "").includes(digits)
        }).slice(0, SHOWN)
    }

    /* The photos picked from the disk */
    pickFiles = event => {

        const { id, files } = this.state
        const picked = Array.from(event.target.files ? event.target.files : []).filter(file => file.type.startsWith("image/"))

        event.target.value = ""

        if (picked.length === 0) {
            return
        }

        /* A new review keeps them until the save */
        if (!id) {
            this.setState({ files: [...files, ...picked.map(file => ({ file, preview: URL.createObjectURL(file) }))] })
            return
        }

        /* An old one sends them right away */
        const form = new FormData()
        form.append("id", id)
        picked.forEach(file => form.append("image", file))

        this.setState({ uploading: true })

        uploadImage(form).then(response => {

            this.setState({ uploading: false })

            if (response && response.status === 200) {
                this.setState({ images: response.data.images.map(image => image.image) })
                this.changed()
            }
            else {
                toast.error("Не удалось загрузить фото")
            }
        }).catch(() => {
            this.setState({ uploading: false })
            toast.error("Не удалось загрузить фото")
        })
    }

    dropFile = index => {

        const { files } = this.state

        URL.revokeObjectURL(files[index].preview)
        this.setState({ files: files.filter((_, position) => position !== index) })
    }

    dropImage = image => {

        const { id } = this.state

        this.setState({ uploading: true })

        removeImage({ id, image }).then(response => {

            this.setState({ uploading: false })

            if (response && response.status === 200) {
                this.setState({ images: response.data.images.map(item => item.image) })
                this.changed()
            }
            else {
                toast.error("Не удалось удалить фото")
            }
        }).catch(() => {
            this.setState({ uploading: false })
            toast.error("Не удалось удалить фото")
        })
    }

    changed = () => {

        const { onSaved } = this.props

        if (onSaved) {
            onSaved()
        }
    }

    save = () => {

        /* Fields */
        const { id, organization, memberID, member, user, rate, comment, date, files, saving } = this.state

        if (saving) {
            return
        }

        if (!organization) {
            toast.error(ERRORS.ORGANIZATION_REQUIRED)
            this.setState({ picking: true })
            return
        }

        if (user.trim().length === 0) {
            this._user.error(ERRORS.INVALID_USER)
            return
        }

        if (!rate) {
            toast.error(ERRORS.INVALID_RATE)
            return
        }

        if (date && moment(date).isAfter(moment())) {
            toast.error(ERRORS.INVALID_DATE)
            return
        }

        const fields = {
            organizationID: organization.id,
            memberID: memberID ? memberID : "",
            member: memberID ? member : "",
            user: user.trim(),
            rate,
            comment: comment.trim(),
            createdAt: date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : ""
        }

        let request = null

        if (id) {
            request = edit({ ...fields, id })
        }
        else {
            const form = new FormData()
            Object.keys(fields).forEach(key => form.append(key, fields[key]))
            files.forEach(item => form.append("image", item.file))
            request = create(form)
        }

        this.setState({ saving: true })

        request.then(response => {

            this.setState({ saving: false })

            if (response && response.status === 200) {
                toast.success(id ? "Отзыв сохранён" : "Отзыв добавлен")
                files.forEach(item => URL.revokeObjectURL(item.preview))
                this.setState({ visible: false })
                this.changed()
                return
            }

            const code = response && response.data ? response.data.code : null
            toast.error(code && ERRORS[code] ? ERRORS[code] : "Что-то пошло не так! Попробуйте позднее")
        }).catch(() => {
            this.setState({ saving: false })
            toast.error("Что-то пошло не так! Попробуйте позднее")
        })
    }

    /* The point of the review — chosen it is a card, otherwise a search over all the points */
    _organization = () => {

        /* Fields */
        const { organization, picking, search, organizationsStatus } = this.state

        if (organization && !picking) {
            return (
                <div className="service-modal-point review-form-point">
                    <Avatar className="service-modal-point-avatar" src={organization.image ? `${env.mediapoint}organizations/${organization.image}` : null} alt={organization.name}>
                        {organizationIcon()}
                    </Avatar>
                    <div className="service-modal-point-text">
                        <span>Точка</span>
                        <b>{organization.name}</b>
                    </div>
                    <div onClick={() => this.setState({ picking: true, search: "" })} className="review-form-change">Сменить</div>
                </div>
            )
        }

        const found = this.found()

        return (
            <div className="review-form-picker">

                <div className="review-form-search">
                    <Icon name="search" size={16} />
                    <input autoFocus placeholder="Название или телефон точки" value={search} onChange={event => this.setState({ search: event.target.value })} />
                    {organization && <div onClick={() => this.setState({ picking: false, search: "" })} className="review-form-cancel">Отмена</div>}
                </div>

                <div className="review-form-results">

                    {organizationsStatus === "loading" && <div className="review-form-results-empty"><LoadingOutlined /></div>}
                    {organizationsStatus === "error" && <div onClick={this.loadOrganizations} className="review-form-results-empty link">Не удалось загрузить точки. Повторить</div>}
                    {organizationsStatus === "ok" && found.length === 0 && <div className="review-form-results-empty">Ничего не нашлось</div>}

                    {organizationsStatus === "ok" && found.map(item =>
                        <div key={`${item.id}`} onClick={() => this.choose(item)} className={`review-form-result ${organization && parseInt(organization.id) === parseInt(item.id) ? "active" : ""}`}>
                            <Avatar className="review-form-result-avatar" src={item.image ? `${env.mediapoint}organizations/${item.image}` : null} alt={item.name}>
                                {organizationIcon()}
                            </Avatar>
                            <b>{item.name}</b>
                            <span>{item.phone}</span>
                        </div>
                    )}

                </div>

            </div>
        )
    }

    /* The master — the team of the point as chips, a master who left stays chosen */
    _team = () => {

        /* Fields */
        const { organization, team, teamStatus, memberID, member } = this.state

        if (!organization) {
            return <div className="wide-modal-hint">Сначала выберите точку</div>
        }

        if (teamStatus === "loading") {
            return <div className="review-form-team"><div className="review-form-chip loading"><LoadingOutlined /> Загружаем команду</div></div>
        }

        const left = memberID && !team.some(item => parseInt(item.memberID) === parseInt(memberID))

        return (
            <div className="review-form-team">

                <div onClick={() => this.setState({ memberID: 0, member: "" })} className={`review-form-chip ${!memberID ? "active" : ""}`}>
                    <Icon name="organization" size={14} width={2} />
                    Точка целиком
                </div>

                {left &&
                    <div className="review-form-chip active" title="Мастер больше не в команде точки">
                        <Icon name="user" size={14} width={2} />
                        {member} · ушёл
                    </div>
                }

                {team.map(item =>
                    <div key={`${item.memberID}`} onClick={() => this.setState({ memberID: item.memberID, member: item.name ? `${item.name}`.trim() : "" })} className={`review-form-chip ${parseInt(memberID) === parseInt(item.memberID) ? "active" : ""}`}>
                        <Avatar className="review-form-chip-avatar" src={item.image ? `${env.mediapoint}members/${item.image}` : null} alt={item.name}>
                            <Icon name="user" size={12} width={2.2} />
                        </Avatar>
                        {item.name ? item.name : item.phone}
                    </div>
                )}

            </div>
        )
    }

    _photos = () => {

        /* Fields */
        const { id, files, images, uploading } = this.state

        return (
            <div className="review-form-photos">

                {!id && files.map((item, index) =>
                    <div key={item.preview} className="review-form-photo">
                        <img src={item.preview} alt="Фото" />
                        <div onClick={() => this.dropFile(index)} className="review-form-photo-remove"><Icon name="close" size={9} width={2.8} /></div>
                    </div>
                )}

                {id > 0 && images.map(image =>
                    <div key={image} className="review-form-photo">
                        <img src={photoOf(image)} alt="Фото" />
                        <div onClick={() => !uploading && this.dropImage(image)} className="review-form-photo-remove"><Icon name="close" size={9} width={2.8} /></div>
                    </div>
                )}

                <label className={`review-form-photo add ${uploading ? "disabled" : ""}`}>
                    {uploading ? <LoadingOutlined /> : <Icon name="image" size={20} width={1.8} />}
                    <span>{uploading ? "Секунду" : "Добавить"}</span>
                    <input type="file" accept="image/*" multiple disabled={uploading} onChange={this.pickFiles} />
                </label>

            </div>
        )
    }

    _body = () => {

        /* Fields */
        const { id, client, user, rate, comment, date, saving } = this.state

        return (
            <div className="wide-modal-body">

                <div className="wide-modal-label">Точка</div>
                {this._organization()}

                <div className="wide-modal-label">Мастер</div>
                {this._team()}

                <div className="wide-modal-label">Оценка</div>
                <div className={`review-form-rate ${rate ? rateAccentOf(rate) : ""}`}>
                    <Stars value={rate} size={34} onChange={value => this.setState({ rate: value })} />
                    <span>{rate ? RATE_WORDS[rate] : "Нажмите на звезду"}</span>
                </div>

                <div className="wide-modal-label">Клиент и текст</div>
                {client && <div className="wide-modal-hint">Отзыв написал клиент из приложения — имя меняется только в подписи, аккаунт остаётся прежним.</div>}
                <Input ref={ref => this._user = ref} label="Имя клиента" maxLength={100} value={user} onChange={event => this.setState({ user: event.target.value })} />
                <Input textarea rows={5} label="Текст отзыва" hint="Пусто — только оценка" maxLength={3000} value={comment} onChange={event => this.setState({ comment: event.target.value })} />

                <div className="wide-modal-label">Дата отзыва</div>
                <div className="wide-modal-hint">Для отзыва из 2ГИС поставьте дату оригинала — лента точки встанет по порядку. Пусто — сейчас.</div>
                <div className="review-form-date">
                    <Icon name="calendar" size={16} width={2} />
                    <input type="datetime-local" max={moment().format("YYYY-MM-DDTHH:mm")} value={date} onChange={event => this.setState({ date: event.target.value })} />
                    {date && !id && <div onClick={() => this.setState({ date: "" })} className="review-form-cancel">Сейчас</div>}
                </div>

                <div className="wide-modal-label">Фото</div>
                {this._photos()}

                <div onClick={this.save} className={`organization-details-button sticky ${saving ? "disabled" : ""}`}>
                    {saving ? <LoadingOutlined /> : (id ? "Сохранить" : "Добавить отзыв")}
                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, id } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal review-form"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>{id ? "Изменить отзыв" : "Новый отзыв"}</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                {visible && this._body()}

            </Modal>
        )
    }
}

export default ReviewForm
